import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';

import { useManualCache } from '../../../hooks/useManualCache';
import theme from '../../../themes/theme';
import {
  DEFAULT_ELEVATION,
  GRID_FORM_FULL_PAPER_STYLE,
} from '../../../utils/constants';
import { openSnackbar } from '../../shared/AktivuSnackbar/snackSlice';
import ConfirmDialog from '../../shared/ConfirmDialog';
import DynamicIcon from '../../shared/DynamicIcon';

import { gridProps } from './gridProps';
import {
  useGetFilesQuery,
  useRemoveFileFromBucketMutation,
} from './s3Slice';

const FilesList = props => {
  const { upload_type, name, default_prefix } = props;

  const dispatch = useDispatch();
  const [updateCache] = useManualCache();

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [confirmData, setConfirmData] = useState();
  const [selected, setSelected] = useState([]);
  const [removeFileFromBucket] = useRemoveFileFromBucketMutation();
  const { showToolbar } = gridProps;
  let files = [];

  const fileOptions = { skip: !upload_type && !default_prefix };

  const { data, isSuccess, isFetching, isError } = useGetFilesQuery(
    { upload_type, prefix: default_prefix },
    fileOptions
  );

  if (isSuccess) {
    const { entities } = data;

    const keys = Object.keys(entities);
    // when no data `keys` has ["undefined"]... :( ugh! who did this!
    if (keys[0] !== 'undefined') {
      files = keys.map(key => entities[key]);
    }
  }

  const columns = [
    {
      field: 'ETag',
      minWidth: 50,
      description: 'Actions',
      renderHeader: () => <strong>{'DOWNLOAD'}</strong>,
      renderCell: params => {
        const row = params?.row;

        /**
         * uses pre-signed url to download a file
         * @param {*} e
         */
        const downloadFile = async e => {
          e.stopPropagation();
          const key = row?.Key;
          const payload = {
            upload_type,
            key,
            command_type: 'GET',
          };

          const response = await axios.post(
            '/api/s3/presigned-url',
            payload
          );

          const { url } = await response.data;

          if (url) {
            window.open(url, '_blank');
          } else {
            dispatch(
              openSnackbar({
                severity: 'error',
                message: 'Unable to download the file',
              })
            );
          }
        };

        return (
          <>
            <Grid item>
              <Tooltip title={'Download File'}>
                <IconButton
                  onClick={downloadFile}
                  style={{
                    color: theme.palette.success.main,
                  }}
                >
                  <DynamicIcon iconName={'CloudDownload'} />
                </IconButton>
              </Tooltip>
            </Grid>
          </>
        );
      },
    },
    {
      field: 'Key',
      minWidth: 400,
      description: 'FileName',
      renderHeader: () => <strong>{'FILE NAME'}</strong>,
      renderCell: params => {
        const row = params?.row;
        const key = row?.Key;
        return key?.split('/').pop();
      },
    },
    {
      field: 'Size',
      minWidth: 200,
      description: 'File size',
      renderHeader: () => <strong>{'FILE SIZE (MB)'}</strong>,
      renderCell: params => {
        const row = params?.row;
        const size = row?.Size;
        if (size) {
          return `${(size / (1024 * 1024)).toFixed(2)}MB`;
        }
        return 0;
      },
    },
    {
      field: 'LastModified',
      minWidth: 400,
      description: 'Uploaded at',
      renderHeader: () => <strong>{'UPLOADED AT'}</strong>,
    },
  ];

  const deleteFiles = async () => {
    let deletedFiles = [];
    // handle await promises returned from the loop
    const promises = selected.map(async row => {
      const f = row?.Key;
      const arr = f?.split('/');
      const fileName = arr.pop();
      const filePrefix = arr.join('/');

      const key = `${filePrefix}/${fileName}`;
      const promise = await removeFileFromBucket({
        key,
        upload_type,
      }).unwrap();
      return promise;
    });

    // resolve promises which should match selected files
    deletedFiles = await Promise.all(promises);

    if (deletedFiles.length === selected.length) {
      setConfirmDialog(false);
      updateCache(['s3Files']);
      dispatch(
        openSnackbar({
          severity: 'success',
          message: 'Files removed successfully',
        })
      );
    } else {
      dispatch(
        openSnackbar({
          severity: 'error',
          message: 'Error removing files',
        })
      );
    }
  };
  const confirmDelete = () => {
    setConfirmData(`${selected.length} selected file(s)?`);
    setConfirmDialog(true);
  };

  const content = (
    <>
      <Typography variant={'h5'}>
        {`${upload_type.replace(/-/g, ' ').toUpperCase()} - ${
          name || 'Files'
        }`}
      </Typography>
      <Button
        disabled={selected.length === 0}
        size="small"
        sx={{
          backgroundColor: selected.length
            ? theme.palette.error.main
            : theme.palette.grey_300.main,
          mb: 2,
          color: theme.palette.white.main,
        }}
        onClick={confirmDelete}
      >
        <DynamicIcon iconName={'Delete'} /> Delete Selected Files
      </Button>
      <DataGrid
        checkboxSelection
        getRowId={row => row.ETag}
        rows={files || []}
        columns={columns}
        components={showToolbar ? { Toolbar: GridToolbar } : {}}
        onSelectionModelChange={ids => {
          const selectedRowData = ids.map(id =>
            files.find(row => row.ETag === id)
          );
          setSelected(selectedRowData);
        }}
        {...gridProps}
      />
      <ConfirmDialog
        deleteRow={deleteFiles}
        setConfirmDialog={setConfirmDialog}
        title={`Confirm Delete?`}
        confirmDialog={confirmDialog}
        confirmData={confirmData}
        confirmDelType={'delete'}
      />
    </>
  );

  if (isFetching) {
    return <>Loading...</>;
  } else if (isError) {
    return <>Error loading data...</>;
  } else if (isSuccess) {
    return (
      <Paper
        elevation={DEFAULT_ELEVATION}
        style={GRID_FORM_FULL_PAPER_STYLE}
      >
        {content}
      </Paper>
    );
  }
};

export default FilesList;
